import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ReactComponent as InfoTooltipIcon } from '@assets/images/Input/info-tooltip.svg'
import { dsColorVar, type DesignSystemColorFamily } from '../tokens/colors'
import type { ColorSelectOption, ColorSelectProps } from './ColorSelect.types'
import {
  COLOR_SELECT_DEMO_ERROR_MESSAGE,
  COLOR_SELECT_DEMO_FILLED_VALUE,
  COLOR_SELECT_DEMO_INFO_MESSAGE,
  COLOR_SELECT_DEMO_LABEL,
  COLOR_SELECT_DEMO_OPTIONS,
  COLOR_SELECT_DEMO_PLACEHOLDER,
} from './ColorSelect.types'
import './ColorSelect.css'

const ColorSelect: React.FC<ColorSelectProps> = ({
  label = COLOR_SELECT_DEMO_LABEL,
  placeholder = COLOR_SELECT_DEMO_PLACEHOLDER,
  options = COLOR_SELECT_DEMO_OPTIONS,
  value,
  defaultValue = '',
  disabled = false,
  error = false,
  helperText,
  optional = false,
  showTooltip = false,
  className = '',
  darkMode = false,
  previewState,
  onChange,
}) => {
  const triggerId = useId()
  const listboxId = useId()
  const fieldRef = useRef<HTMLDivElement>(null)
  const [internalValue, setInternalValue] = useState<
    DesignSystemColorFamily | ''
  >(defaultValue)
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const isPreview = previewState != null
  const isDisabled = disabled || previewState === 'disabled'
  const isError = error || previewState === 'error'
  const isOptional = optional || previewState === 'optional'
  const hasTooltip = showTooltip || previewState === 'with-tooltip'

  const resolvedValue = useMemo(() => {
    if (isPreview) {
      if (
        previewState === 'filled' ||
        previewState === 'disabled' ||
        previewState === 'error' ||
        previewState === 'with-info'
      ) {
        return COLOR_SELECT_DEMO_FILLED_VALUE
      }

      return ''
    }

    return value ?? internalValue
  }, [internalValue, isPreview, previewState, value])

  const selectedOption = useMemo(
    () => options.find((option) => option.value === resolvedValue),
    [options, resolvedValue],
  )

  const hasSelection = Boolean(selectedOption)

  const resolvedHelperText = useMemo(() => {
    if (helperText != null) {
      return helperText
    }

    if (previewState === 'error') {
      return COLOR_SELECT_DEMO_ERROR_MESSAGE
    }

    if (previewState === 'with-info') {
      return COLOR_SELECT_DEMO_INFO_MESSAGE
    }

    return undefined
  }, [helperText, previewState])

  const isMenuVisible =
    isPreview && previewState === 'expanded' ? true : menuOpen

  const isOpenTrigger =
    isMenuVisible && (previewState === 'expanded' || (!isPreview && menuOpen))

  const emitChange = useCallback(
    (nextValue: DesignSystemColorFamily) => {
      if (value === undefined) {
        setInternalValue(nextValue)
      }

      onChange?.(nextValue)
    },
    [onChange, value],
  )

  const handleToggleMenu = useCallback(() => {
    if (isPreview || isDisabled) {
      return
    }

    setMenuOpen((open) => {
      if (open) {
        setActiveIndex(-1)
        return false
      }

      return true
    })
  }, [isDisabled, isPreview])

  const handleSelectOption = useCallback(
    (optionValue: DesignSystemColorFamily) => {
      if (isPreview || isDisabled) {
        return
      }

      emitChange(optionValue)
      setMenuOpen(false)
      setActiveIndex(-1)
    },
    [emitChange, isDisabled, isPreview],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (isPreview || isDisabled) {
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (!menuOpen) {
          setMenuOpen(true)
          setActiveIndex(0)
          return
        }

        setActiveIndex((index) =>
          Math.min(index + 1, Math.max(options.length - 1, 0)),
        )
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (!menuOpen) {
          setMenuOpen(true)
          setActiveIndex(options.length - 1)
          return
        }

        setActiveIndex((index) => Math.max(index - 1, 0))
        return
      }

      if (event.key === 'Enter' && menuOpen && activeIndex >= 0) {
        event.preventDefault()
        const option = options[activeIndex]
        if (option) {
          handleSelectOption(option.value)
        }
        return
      }

      if (event.key === 'Escape' && menuOpen) {
        event.preventDefault()
        setMenuOpen(false)
        setActiveIndex(-1)
      }
    },
    [activeIndex, handleSelectOption, isDisabled, isPreview, menuOpen, options],
  )

  useEffect(() => {
    if (isPreview || !menuOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      if (
        fieldRef.current &&
        !fieldRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false)
        setActiveIndex(-1)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [isPreview, menuOpen])

  const classNames = [
    'ds-color-select',
    isDisabled ? 'ds-color-select--disabled' : '',
    isError ? 'ds-color-select--error' : '',
    darkMode ? 'ds-color-select--dark' : '',
    isPreview ? 'ds-color-select--preview' : '',
    previewState ? `ds-color-select--preview-${previewState}` : '',
    hasSelection ? 'ds-color-select--filled' : '',
    isOpenTrigger ? 'ds-color-select--open' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const renderSwatch = (
    option: ColorSelectOption,
    size: 'menu' | 'trigger' = 'menu',
  ): React.ReactNode => (
    <span
      className={`ds-color-select__swatch${
        size === 'trigger' ? ' ds-color-select__swatch--trigger' : ''
      }`}
      style={{ background: dsColorVar(option.value, option.swatchShade) }}
      aria-hidden="true"
    />
  )

  return (
    <div className={classNames}>
      <div className="ds-color-select__label-row">
        <label className="ds-color-select__label" htmlFor={triggerId}>
          {label}
          {isOptional ? (
            <span className="ds-color-select__optional"> (Optional)</span>
          ) : null}
        </label>
        {hasTooltip ? (
          <span
            className="ds-color-select__tooltip-trigger"
            title={label}
            aria-label={`More information about ${label}`}
          >
            <InfoTooltipIcon />
          </span>
        ) : null}
      </div>

      <div
        className={`ds-color-select__field${
          previewState === 'focused'
            ? ' ds-color-select__field--focused-preview'
            : ''
        }`}
        ref={fieldRef}
      >
        <div className="ds-color-select__control">
          <button
            id={triggerId}
            type="button"
            className="ds-color-select__trigger"
            aria-haspopup="listbox"
            aria-expanded={isMenuVisible}
            aria-controls={listboxId}
            disabled={isPreview || isDisabled}
            tabIndex={isPreview ? -1 : 0}
            onClick={handleToggleMenu}
            onKeyDown={handleKeyDown}
          >
            <span className="ds-color-select__trigger-content">
              {selectedOption ? renderSwatch(selectedOption, 'trigger') : null}
              <span
                className={`ds-color-select__value${
                  hasSelection ? ' ds-color-select__value--selected' : ''
                }`}
              >
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            </span>
            <i
              className="mgc mgc_down_small_fill ds-color-select__chevron"
              aria-hidden="true"
            />
          </button>
        </div>

        {isMenuVisible ? (
          <div
            id={listboxId}
            className="ds-color-select__menu"
            role="listbox"
            aria-labelledby={triggerId}
          >
            <div className="ds-color-select__menu-scroll">
              {options.map((option, index) => {
                const isSelected = resolvedValue === option.value
                const isActive = index === activeIndex

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`ds-color-select__option${
                      isSelected ? ' ds-color-select__option--selected' : ''
                    }${isActive ? ' ds-color-select__option--active' : ''}`}
                    role="option"
                    aria-selected={isSelected}
                    disabled={isPreview || isDisabled}
                    onMouseDown={(event) => {
                      event.preventDefault()
                    }}
                    onClick={() => handleSelectOption(option.value)}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    {renderSwatch(option)}
                    <span className="ds-color-select__option-label">
                      {option.label}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>

      {resolvedHelperText ? (
        <p
          className={`ds-color-select__helper${
            isError ? ' ds-color-select__helper--error' : ''
          }`}
        >
          {resolvedHelperText}
        </p>
      ) : null}
    </div>
  )
}

export default ColorSelect
